/**
 * The chrome every staff page shares: one sign-in gate, one nav bar.
 *
 * Signed out, it renders the login form under the page's own heading. Signed
 * in, it renders the admin nav and the page. The scan screen is deliberately
 * not one of these pages — it is the public door UI and carries no admin
 * chrome; the nav only *links* to it.
 */

import type { ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAdminBranch } from "../context/AdminBranchContext";
import { useDevice } from "../context/DeviceContext";
import { useStaff } from "../context/StaffContext";
import StaffLoginForm from "./StaffLoginForm";
import "./AdminLayout.css";

/**
 * `also`: other exact paths the link counts as active on. "Kiosk" is `/` — the
 * chooser or the scan screen, or a redirect to `/find` on a type-mode device
 * (D-019) — and
 * `/scan` is the classic badge screen, so it lights up on all three.
 */
const NAV_LINKS: { to: string; label: string; end: boolean; also?: string[] }[] = [
  { to: "/", label: "Kiosk", end: true, also: ["/scan", "/find"] },
  { to: "/today", label: "Today", end: false },
  { to: "/badges", label: "Badges", end: false },
  { to: "/setup", label: "Device", end: false },
];

/**
 * The acting-branch picker, header-sized. Only pages whose content is scoped
 * by branch mount it — the setup screen has its own branch step (it registers
 * the *device*), and the device pages are branch-locked by their token.
 */
export function HeaderBranchSelect() {
  const { branches, branchId, loading, setBranchId } = useAdminBranch();

  return (
    <span className="branch-pick">
      <label className="spec" htmlFor="header-branch">
        Branch
      </label>
      <select
        id="header-branch"
        value={branchId}
        disabled={branches.length === 0}
        onChange={(event) =>
          setBranchId(event.target.value === "" ? "" : Number(event.target.value))
        }
      >
        <option value="">
          {loading
            ? "Loading…"
            : branches.length === 0
              ? "No branches"
              : "Choose a branch…"}
        </option>
        {branches.map((branch) => (
          <option key={branch.branch_id} value={branch.branch_id}>
            {branch.branch_name}
          </option>
        ))}
      </select>
    </span>
  );
}

/**
 * The white topbar in nav form. `trailing` lets a screen keep its own status
 * line (the today board's branch + date) without stacking a second bar.
 */
export function AdminNav({ trailing }: { trailing?: ReactNode }) {
  const { staff, signOut } = useStaff();
  const { pathname } = useLocation();

  return (
    <header className="topbar admin-nav no-print">
      <span className="wordmark">Zebra Check-In</span>

      <nav className="admin-links" aria-label="Admin sections">
        {NAV_LINKS.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            className={({ isActive }) =>
              `admin-link${isActive || link.also?.includes(pathname) ? " is-active" : ""}`
            }
          >
            {link.label}
          </NavLink>
        ))}
      </nav>

      <span className="spacer" />
      {trailing}

      <span className="spec admin-who">signed in: {staff?.displayName || "staff"}</span>
      <button type="button" className="btn-ghost" onClick={() => signOut()}>
        Sign out
      </button>
    </header>
  );
}

interface AdminLayoutProps {
  /** Small tracked-out label above the heading on the sign-in gate. */
  eyebrow?: string;
  /** The page's own heading, kept in front of staff while they sign in. */
  heading: ReactNode;
  /** One line of context under the heading on the gate. */
  intro?: ReactNode;
  /** Anything the page needs shown above the form (e.g. an eviction notice). */
  notice?: ReactNode;
  /** Mount the acting-branch picker in the nav. Opt-in per page. */
  branchPicker?: boolean;
  children: ReactNode;
}

export default function AdminLayout({
  eyebrow = "Staff tool",
  heading,
  intro,
  notice,
  branchPicker = false,
  children,
}: AdminLayoutProps) {
  const { isSignedIn } = useStaff();
  const { device } = useDevice();
  const navigate = useNavigate();

  if (isSignedIn) {
    return (
      <div className="screen">
        <div className="zebra-field" aria-hidden="true" />
        <AdminNav trailing={branchPicker ? <HeaderBranchSelect /> : undefined} />
        {children}
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="zebra-field" aria-hidden="true" />

      <header className="topbar">
        <span className="wordmark">Zebra Check-In</span>
        <span className="spacer" />
        {device && (
          <button type="button" className="btn-ghost" onClick={() => navigate("/")}>
            Back to kiosk
          </button>
        )}
      </header>

      <main className="admin-stage">
        <div className="admin-panel">
          {notice}
          <div>
            <p className="eyebrow admin-panel-eyebrow">{eyebrow}</p>
            <h1 className="admin-panel-heading">{heading}</h1>
            {intro && <p className="admin-panel-sub">{intro}</p>}
          </div>
          <StaffLoginForm />
        </div>
      </main>
    </div>
  );
}
